import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import $api from '../api'
import type { Song } from './playlistSlice'

export interface UserPlaylistSong extends Partial<Song> {
  _id: string
  title?: string
  artist?: string
  file?: string
  image?: string
  imageUrl?: string
  duration?: number
}

export interface UserPlaylist {
  _id: string
  user: string
  title: string
  description?: string
  image?: string
  songs: UserPlaylistSong[]
  createdAt: number
  updatedAt?: number
}

interface State {
  items: UserPlaylist[]
  isLoading: boolean
  isSaving: boolean
  error: string | null
}

export const initialState: State = {
  items: [],
  isLoading: false,
  isSaving: false,
  error: null,
}

type CreatePlaylistPayload = {
  title: string
  description?: string
  image?: string
}

type UpdatePlaylistPayload = {
  id: string
  title?: string
  description?: string
  image?: string
}

type PlaylistTrackPayload = {
  playlistId: string
  songId: string
}

export const attemptGetUserPlaylists = createAsyncThunk<UserPlaylist[], void>(
  'user-playlists/list',
  async () => {
    const response = await $api.post('user-playlists/list')
    return response.data
  },
)

export const attemptGetUserPlaylist = createAsyncThunk<UserPlaylist, string>(
  'user-playlists/get',
  async (id) => {
    const response = await $api.post('user-playlists/get', { id })
    return response.data
  },
)

export const attemptCreateUserPlaylist = createAsyncThunk<
  UserPlaylist,
  CreatePlaylistPayload
>('user-playlists/create', async (payload) => {
  const response = await $api.post('user-playlists/create', payload)
  return response.data
})

export const attemptUpdateUserPlaylist = createAsyncThunk<
  UserPlaylist,
  UpdatePlaylistPayload
>('user-playlists/update', async (payload) => {
  const response = await $api.post('user-playlists/update', payload)
  return response.data
})

export const attemptDeleteUserPlaylist = createAsyncThunk<void, string>(
  'user-playlists/delete',
  async (id) => {
    await $api.post('user-playlists/delete', { id })
  },
)

export const attemptAddSongToUserPlaylist = createAsyncThunk<
  UserPlaylistSong,
  PlaylistTrackPayload
>('user-playlists/add/song', async (payload) => {
  const response = await $api.post('user-playlists/add/song', payload)
  return response.data
})

export const attemptRemoveSongFromUserPlaylist = createAsyncThunk<
  UserPlaylist,
  PlaylistTrackPayload
>('user-playlists/remove/song', async (payload) => {
  const response = await $api.post('user-playlists/remove/song', payload)
  return response.data
})

export const attemptUploadUserPlaylistImage = createAsyncThunk<string, File>(
  'user-playlists/upload',
  async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await $api.post('user-playlists/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    return typeof response.data === 'string' ? response.data : response.data?.filename
  },
)

const userPlaylistsSlice = createSlice({
  name: 'userPlaylists',
  initialState,
  reducers: {
    clearUserPlaylistsError(state) {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(attemptGetUserPlaylists.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(attemptGetUserPlaylists.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.items = payload ?? []
      })
      .addCase(attemptGetUserPlaylists.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to load playlists'
      })
      .addCase(attemptGetUserPlaylist.fulfilled, (state, { payload }) => {
        const index = state.items.findIndex((item) => item._id === payload._id)
        if (index >= 0) {
          state.items[index] = { ...state.items[index], ...payload }
        } else {
          state.items.unshift(payload)
        }
      })
      .addCase(attemptCreateUserPlaylist.pending, (state) => {
        state.isSaving = true
        state.error = null
      })
      .addCase(attemptCreateUserPlaylist.fulfilled, (state, { payload }) => {
        state.isSaving = false
        state.items = [payload, ...state.items.filter((item) => item._id !== payload._id)]
      })
      .addCase(attemptCreateUserPlaylist.rejected, (state, action) => {
        state.isSaving = false
        state.error = action.error.message || 'Failed to create playlist'
      })
      .addCase(attemptUpdateUserPlaylist.fulfilled, (state, { payload }) => {
        state.items = state.items.map((item) =>
          item._id === payload._id ? { ...item, ...payload } : item,
        )
      })
      .addCase(attemptDeleteUserPlaylist.fulfilled, (state, action) => {
        const id = action.meta.arg
        state.items = state.items.filter((item) => item._id !== id)
      })
      .addCase(attemptAddSongToUserPlaylist.fulfilled, (state, action) => {
        const payload = action.payload
        const playlistId = action.meta.arg.playlistId
        const playlist = state.items.find((item) => item._id === playlistId)
        if (!playlist) return

        if (!playlist.songs.some((song) => song._id === payload._id)) {
          playlist.songs.push(payload)
        }
      })
      .addCase(attemptRemoveSongFromUserPlaylist.fulfilled, (state, action) => {
        const updatedPlaylist = action.payload
        const index = state.items.findIndex((item) => item._id === updatedPlaylist._id)
        if (index >= 0) {
          state.items[index] = updatedPlaylist
        }
      })
  },
})

export const { clearUserPlaylistsError } = userPlaylistsSlice.actions

export default userPlaylistsSlice.reducer
